import { Link, useNavigate } from 'react-router-dom'
import { Bell, Search, User, Menu, Settings } from 'lucide-react'
// DEFERRED for launch — theme switcher disabled (dead control, dark-only app).
// Re-enable after Plan 38. import { ThemeToggle } from './ThemeToggle'
import { WalletBadge } from './WalletBadge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ConnectionStatus } from '@/components/shared/ConnectionStatus'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { useBuildStore } from '@/stores/buildStore'
import { useWsStore } from '@/stores/wsStore'
import { signOut } from '@/lib/auth'
import { queryClient } from '@/lib/queryClient'

interface TopBarProps {
  isCollapsed: boolean
  isMobile: boolean
  toggleMobileSidebar: () => void
}

export function TopBar({ isCollapsed, isMobile, toggleMobileSidebar }: TopBarProps) {
  const user = useAuthStore((s) => s.user)
  const profile = useUserStore((s) => s.profile)
  const navigate = useNavigate()

  const handleSignOut = async () => {
    // Clear all cached data before signing out to prevent leaking to next session
    queryClient.clear()
    useUserStore.getState().reset()
    useBuildStore.getState().reset()
    useWsStore.getState().resetReconnect()
    localStorage.removeItem('qyngent_active_project')
    localStorage.removeItem('qyngent:lastProject')
    localStorage.removeItem('qyngent:lastApplication')
    useAuthStore.getState().logout()
    await signOut()
  }

  const sidebarWidth = isCollapsed ? 80 : 280

  return (
    <div
      className="h-16 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-white/10 fixed top-0 right-0 z-30 flex items-center justify-between px-6 transition-all duration-300"
      style={{ left: isMobile ? 0 : sidebarWidth }}
    >
      <div className="flex items-center flex-1 min-w-0">
        {isMobile && (
          <button onClick={toggleMobileSidebar} className="p-2 -ml-2 mr-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors shrink-0">
            <Menu className="w-6 h-6" />
          </button>
        )}
        <button
          onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }))}
          className="relative flex items-center w-full max-w-xl min-w-0 pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-gray-400 hover:border-white/20 transition-colors text-left overflow-hidden whitespace-nowrap"
          aria-label="Open command palette"
        >
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          Search pages, actions...
          <kbd className="ml-auto hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-white/5 text-[10px] text-gray-400 font-mono border border-white/10">
            ⌘K
          </kbd>
        </button>
      </div>

      <div className="flex items-center space-x-2 sm:space-x-4 shrink-0">
        <div className="hidden sm:block">
          <ConnectionStatus />
        </div>

        <WalletBadge />

        {/* DEFERRED for launch — the theme switcher is a dead control: the app is
            hardcoded dark-only (~7000 color utilities across ~400 files), so
            light/system have no visible effect. Re-enable after the semantic-token
            migration. See docs/plans/38-LIGHT-DARK-THEME-MIGRATION.md.
        <div className="hidden sm:block">
          <ThemeToggle />
        </div>
        */}

        <Link to="/dashboard">
          <button className="p-2 rounded-full hover:bg-white/5 text-gray-400 hover:text-white transition-colors relative">
            <Bell className="w-5 h-5" />
            <span data-testid="dynamic" className="absolute top-1.5 right-1.5 w-2 h-2 bg-lime rounded-full animate-pulse" />
          </button>
        </Link>

        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger className="outline-hidden">
              <div className="flex items-center space-x-3 pl-4 border-l border-white/10">
                <div className="text-right hidden sm:block">
                  <div className="text-sm font-medium text-white">{profile?.full_name || user.email?.split('@')[0] || 'You'}</div>
                  <div className="text-xs text-gray-400">{profile?.role_title || profile?.tier || 'User'}</div>
                </div>
                <div data-testid="user-avatar" className="w-9 h-9 rounded-full bg-linear-to-br from-lime to-cyan p-px">
                  {profile?.avatar_url ? (
                    <img src={profile.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <div className="w-full h-full rounded-full bg-[#0a0a0a] flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/dashboard')}>Dashboard</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/projects')}>Projects</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          // Anonymous user — replace the fake "Dev User" widget with real
          // Sign in / Sign up CTAs so the UI stops lying about who's
          // logged in (the Lane 1 anon path hits this state).
          <div className="flex items-center gap-2 pl-4 border-l border-white/10">
            <Link
              to="/login"
              className="text-sm font-medium text-gray-300 hover:text-white px-3 py-1.5 rounded-md hover:bg-white/5 transition-colors"
            >
              Sign in
            </Link>
            <Link
              to="/login?signup=1"
              className="text-sm font-semibold text-black bg-lime hover:bg-lime/90 px-3 py-1.5 rounded-md transition-colors"
              data-testid="topbar-signup-cta"
            >
              Sign up
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
